// V6.17 · Remessas CNAB Safra 400 — fonte única db.titles, elegibilidade real,
// pré-validação, seleção total do filtro, geração idempotente e histórico completo.
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileDown, Send, FileText, Search, AlertCircle, Trash2, RefreshCw, CheckCircle2, Undo2, Mail } from 'lucide-react';
import { NumeracaoSafra } from './NumeracaoSafra';

const money = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const dt = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '—');

// Estados distintos: gerar/baixar não é envio, e enviar não é registro no banco.
const ESTADO_REMESSA = {
  generated: 'GERADO',
  sent: 'ENVIADO',
  registered: 'REGISTRADO',
  rejected: 'REJEITADO',
  nao_remetido: 'NÃO REMETIDO',
};

const BUCKETS = [
  { key: '', label: 'Todos' },
  { key: 'elegivel', label: 'Elegíveis' },
  { key: 'pendencia_cadastral', label: 'Pendências cadastrais' },
  { key: 'ja_enviado', label: 'Já enviados' },
  { key: 'com_boleto', label: 'Com boleto' },
  { key: 'invalido', label: 'Inválidos' },
];

export default function DnaPayRemessas() {
  const [tab, setTab] = useState('gerar');
  const [data, setData] = useState(null);
  const [remessas, setRemessas] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [validation, setValidation] = useState(null);
  const [preview, setPreview] = useState(null);
  const [gerado, setGerado] = useState(null);
  const [bloqueio, setBloqueio] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [renum, setRenum] = useState(null);
  const [numeracao, setNumeracao] = useState(null);
  const [f, setF] = useState({ ano: '', mes: '', turma: '', categoria: '', status: '', origem: '', bucket: '', q: '' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [f, page, pageSize]);

  const load = async () => {
    setLoading(true);
    try {
      const [el, hist] = await Promise.all([
        api.get('/dnapay/safra/remessa/elegiveis', { params }),
        api.get('/dnapay/safra/remessas', { params: { limit: 50 } }),
      ]);
      setData(el.data);
      setRemessas(hist.data?.remessas || hist.data || []);
    } catch (err) { toast.error('Erro ao carregar: ' + (err?.response?.data?.detail || err.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page, f]);

  const items = data?.items || [];
  const cards = data?.cards || {};
  const selValue = useMemo(
    () => items.filter((i) => sel.has(i.id)).reduce((a, i) => a + Number(i.valor_final || 0), 0),
    [items, sel]
  );

  const selSemNumero = useMemo(
    () => items.filter((i) => sel.has(i.id) && !i.nosso_numero).length,
    [items, sel]
  );

  // Qualquer mudança de seleção invalida a validação/prévia anterior do lote.
  const invalidarLote = () => { setValidation(null); setPreview(null); setGerado(null); };

  const toggle = (id) => {
    const n = new Set(sel);
    n.has(id) ? n.delete(id) : n.add(id);
    setSel(n);
    invalidarLote();
  };

  const selectAllEligible = async () => {
    try {
      const { data: d } = await api.get('/dnapay/safra/remessa/elegiveis', { params: { ...params, ids_only: true } });
      setSel(new Set(d.eligible_ids || []));
      invalidarLote();
      toast.success(`${d.total_eligible} títulos elegíveis do filtro selecionados · ${money(d.total_value)}`);
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  const validar = async () => {
    if (!sel.size) { toast.error('Selecione títulos'); return; }
    setBusy('validar');
    try {
      const ids = Array.from(sel);
      const [{ data: v }, { data: p }] = await Promise.all([
        api.post('/dnapay/safra/remessa/validar', { titulo_ids: ids }),
        api.post('/dnapay/safra/remessa/preview', { titulo_ids: ids }),
      ]);
      setValidation(v);
      setPreview(p);
      setGerado(null);
      toast.success(`${v.validos} válidos · ${v.bloqueados} bloqueados · arquivo previsto ${p.arquivo_previsto}`);
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
    finally { setBusy(''); }
  };

  const gerar = async (somenteValidos = false) => {
    if (!sel.size) { toast.error('Selecione títulos'); return; }
    setBusy('gerar');
    try {
      const { data: r } = await api.post('/dnapay/safra/remessa', {
        titulo_ids: Array.from(sel), strict_block: !somenteValidos });
      setGerado(r);
      toast.success(`${r.filename} · remessa CNAB ${r.numero_remessa_cnab} · ${r.qtd_titulos} títulos · ${money(r.valor_total)}`);
      setSel(new Set());
      setValidation(null);
      setPreview(null);
      await load();
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/dnapay/safra/remessa/${r.id}/download`, '_blank');
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === 'object') {
        setBloqueio(d);
        toast.error(d.detail || 'Falha ao gerar remessa');
      } else toast.error('Erro: ' + (d || err.message));
    } finally { setBusy(''); }
  };

  const renumerar = async () => {
    if (!sel.size) { toast.error('Selecione títulos'); return; }
    const motivo = window.prompt('Motivo da renumeração (mín. 8 caracteres):', 'Numeração antiga anterior ao piso informado pelo banco');
    if (!motivo || motivo.trim().length < 8) { toast.error('Informe um motivo com ao menos 8 caracteres'); return; }
    setBusy('renumerar');
    try {
      const { data: d } = await api.post('/dnapay/safra/numeracao/renumerar', {
        title_ids: Array.from(sel), motivo });
      setRenum(d);
      setValidation(null); setPreview(null); setGerado(null);
      toast.success(`${d.renumerados} renumerados · ${d.bloqueados.length} bloqueados`);
      await load();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail?.detail || err.message)); }
    finally { setBusy(''); }
  };

  const enviarBoletosRemessa = async (det) => {
    const id = det?.remessa?.id;
    if (!id) return;
    if (!window.confirm(`Enviar por e-mail os boletos desta remessa (${det.titulos.length} título(s))?`)) return;
    try {
      const { data } = await api.post('/dnapay/boletos/enviar-email-lote', { remessa_id: id });
      toast.info(`Enviando ${data.total} boleto(s) por e-mail…`);
      const acompanhar = async () => {
        const { data: st } = await api.get(`/dnapay/boletos/enviar-email-lote/${data.job_id}`);
        if (st.status === 'em_execucao') { setTimeout(acompanhar, 5000); return; }
        toast.success(`${st.enviados} boleto(s) enviado(s)`
          + (st.falhas?.length ? ` · ${st.falhas.length} falha(s)` : ''));
      };
      setTimeout(acompanhar, 5000);
    } catch {
      toast.error('Não foi possível iniciar o envio por e-mail.');
    }
  };

  const baixarBoletosLote = async (det) => {
    const ids = (det?.titulos || []).map((t) => t.id);
    if (!ids.length) { toast.error('Remessa sem títulos'); return; }
    toast.info(`Preparando ${ids.length} boleto(s) em um PDF único…`);
    try {
      const r = await api.post('/dnapay/boletos/pdf-lote-safra', { titulo_ids: ids },
        { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('PDF em lote pronto.');
    } catch (err) {
      toast.error('Não foi possível gerar o PDF em lote.');
    }
  };

  const verTitulos = async (rem) => {
    try {
      const { data: d } = await api.get(`/dnapay/safra/remessa/${rem.id}/titulos`);
      setDetalhe(d);
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  const reprocessar = async (rem) => {
    if (!window.confirm('Liberar os títulos rejeitados/bloqueados desta remessa para nova tentativa?')) return;
    try {
      const { data: d } = await api.post(`/dnapay/safra/remessa/${rem.id}/reprocessar-rejeitados`);
      toast.success(`${d.liberados} título(s) liberado(s) para nova remessa`);
      await load();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  const cancelar = async (rem) => {
    const motivo = window.prompt('Motivo do cancelamento da remessa?');
    if (!motivo) return;
    try {
      await api.post(`/dnapay/safra/remessas/${rem.id}/cancelar`, { motivo });
      toast.success('Remessa cancelada · títulos liberados');
      await load();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  const reverter = async (rem) => {
    const motivo = window.prompt('Motivo da REVERSÃO da remessa? (arquivo e histórico são preservados)');
    if (!motivo) return;
    try {
      const { data: d } = await api.post(`/dnapay/safra/remessas/${rem.id}/reverter`, { motivo });
      toast.success(`Remessa revertida · ${d.titulos_liberados} título(s) voltaram a elegíveis`);
      await load();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  const excluir = async (rem) => {
    const motivo = window.prompt('Excluir definitivamente a remessa? Os títulos NÃO são apagados — apenas liberados. Informe o motivo:');
    if (!motivo) return;
    try {
      const { data: d } = await api.delete(`/dnapay/safra/remessa/${rem.id}`);
      toast.success(`Remessa ${d.removed_remessa || ''} excluída · ${d.titulos_liberados} título(s) liberados`);
      await load();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
  };

  const CardBox = ({ label, value, sub, bucket, tone = 'slate' }) => (
    <button onClick={() => { setF({ ...f, bucket: bucket || '' }); setPage(1); }}
      data-testid={`rem-card-${bucket || 'todos'}`}
      className={`text-left border rounded-lg p-3 transition-colors hover:border-purple-400 ${
        f.bucket === (bucket || '') ? 'border-purple-500 bg-purple-50' : 'bg-white'}`}>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold text-${tone}-700`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </button>
  );

  return (
    <div className="p-4 space-y-4" data-testid="dnapay-remessas">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Remessas CNAB · Safra 400</h1>
          <p className="text-sm text-muted-foreground">Fonte única: títulos do financeiro (db.titles). Gerar remessa não altera o status do título.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={tab === 'gerar' ? 'default' : 'outline'} onClick={() => setTab('gerar')} data-testid="rem-tab-gerar">Gerar remessa</Button>
          <Button variant={tab === 'hist' ? 'default' : 'outline'} onClick={() => setTab('hist')} data-testid="rem-tab-hist">Histórico</Button>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="rem-reload">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {tab === 'gerar' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="rem-cards">
            <CardBox label="Total avaliado" value={cards.total_avaliado ?? 0} bucket="" />
            <CardBox label="Títulos elegíveis" value={cards.elegiveis ?? 0} bucket="elegivel" tone="green" />
            <CardBox label="Valor elegível" value={money(cards.valor_elegivel)} bucket="elegivel" tone="green" />
            <CardBox label="Selecionados" value={sel.size} sub={money(selValue)} bucket="" tone="purple" />
            <CardBox label="Pendências cadastrais" value={cards.pendencias_cadastrais ?? 0} bucket="pendencia_cadastral" tone="amber" />
            <CardBox label="Já enviados ao Safra" value={cards.ja_enviados ?? 0} bucket="ja_enviado" tone="blue" />
            <CardBox label="Com boleto ativo" value={cards.com_boleto ?? 0} bucket="com_boleto" tone="blue" />
          </div>

          <Card>
            <CardContent className="pt-4 space-y-3">
              <div className="flex flex-wrap gap-2 items-end">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                  <Input className="pl-8 w-72" placeholder="Nome, matrícula, CPF, nosso número, title_id"
                    value={f.q} onChange={(e) => { setF({ ...f, q: e.target.value }); setPage(1); }} data-testid="rem-search" />
                </div>
                <Input className="w-24" placeholder="Ano" value={f.ano} onChange={(e) => { setF({ ...f, ano: e.target.value }); setPage(1); }} data-testid="rem-f-ano" />
                <Input className="w-24" placeholder="Mês" value={f.mes} onChange={(e) => { setF({ ...f, mes: e.target.value }); setPage(1); }} data-testid="rem-f-mes" />
                <Input className="w-40" placeholder="Categoria" value={f.categoria} onChange={(e) => { setF({ ...f, categoria: e.target.value }); setPage(1); }} data-testid="rem-f-categoria" />
                <Input className="w-36" placeholder="Turma (id)" value={f.turma} onChange={(e) => { setF({ ...f, turma: e.target.value }); setPage(1); }} data-testid="rem-f-turma" />
                <Input className="w-32" placeholder="Status" value={f.status} onChange={(e) => { setF({ ...f, status: e.target.value }); setPage(1); }} data-testid="rem-f-status" />
                <Input className="w-40" placeholder="Origem" value={f.origem} onChange={(e) => { setF({ ...f, origem: e.target.value }); setPage(1); }} data-testid="rem-f-origem" />
                <select className="border rounded h-9 px-2 text-sm bg-white" value={f.bucket}
                  onChange={(e) => { setF({ ...f, bucket: e.target.value }); setPage(1); }} data-testid="rem-f-bucket">
                  {BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
                <Button variant="outline" onClick={() => { setF({ ano: '', mes: '', turma: '', categoria: '', status: '', origem: '', bucket: '', q: '' }); setPage(1); }}>Limpar</Button>
              </div>

              <NumeracaoSafra qtdNovos={selSemNumero} onChange={setNumeracao} />

              <div className="flex flex-wrap gap-2 items-center border-t pt-3">
                <Button variant="outline" onClick={selectAllEligible} data-testid="rem-select-all">
                  Selecionar todos os elegíveis do filtro
                </Button>
                <Button variant="outline" onClick={renumerar} disabled={busy === 'renumerar' || !sel.size}
                  data-testid="rem-renumerar">
                  {busy === 'renumerar' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Corrigir numeração antiga
                </Button>
                <Button variant="outline" onClick={() => setSel(new Set())} disabled={!sel.size}>Limpar seleção</Button>
                <span className="text-sm font-medium" data-testid="rem-sel-summary">
                  {sel.size} selecionados · {money(selValue)}
                </span>
                <div className="flex-1" />
                <Button variant="outline" onClick={validar} disabled={busy === 'validar' || !sel.size} data-testid="rem-validar">
                  {busy === 'validar' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Validar
                </Button>
                <Button onClick={() => gerar(false)} disabled={busy === 'gerar' || !sel.size} data-testid="rem-gerar">
                  {busy === 'gerar' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Send className="w-4 h-4 mr-2" /> VALIDAR E GERAR REMESSA
                </Button>
              </div>

              {renum && (
                <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs space-y-1" data-testid="rem-renum-result">
                  <div className="text-sm font-semibold text-amber-900">
                    Renumeração · {renum.renumerados} corrigidos
                    {renum.faixa && <> · faixa {renum.faixa.inicio} a {renum.faixa.fim}</>}
                  </div>
                  {(renum.trocas || []).slice(0, 20).map((t) => (
                    <div key={t.title_id}>
                      {t.aluno || t.title_id}: <b>{t.de || '—'}</b> → <b>{t.para}</b>
                    </div>
                  ))}
                  {(renum.bloqueados || []).map((b) => (
                    <div key={b.title_id} className="text-red-700">Bloqueado {b.title_id}: {b.motivo}</div>
                  ))}
                  {renum.aviso && <div className="text-amber-800">{renum.aviso}</div>}
                  <Button size="sm" variant="ghost" onClick={() => setRenum(null)}>Fechar</Button>
                </div>
              )}

              {preview && (
                <div className="border border-purple-200 bg-purple-50 rounded p-3 text-xs space-y-1" data-testid="rem-preview">
                  <div className="text-sm font-semibold text-purple-900">Confira antes de gerar</div>
                  <div>Títulos selecionados: <b data-testid="rem-preview-qtd">{preview.titulos_selecionados}</b></div>
                  <div>Valor total: <b>{money(preview.valor_total)}</b></div>
                  <div>Vencimento(s): {(preview.vencimentos || []).join(' · ') || '—'}</div>
                  <div>Arquivo previsto: <b data-testid="rem-preview-arquivo">{preview.arquivo_previsto}</b></div>
                  <div>Próxima remessa CNAB: <b>{String(preview.proxima_remessa_cnab).padStart(3, '0')}</b></div>
                  <div>Registros previstos: <b>{preview.registros_previstos}</b> (header + títulos + trailer)</div>
                </div>
              )}

              {gerado && (
                <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs space-y-1" data-testid="rem-gerado">
                  <div className="text-sm font-semibold text-emerald-900">{gerado.filename} pronto para o Safra Empresas</div>
                  {Object.entries({
                    'CNAB 400 Safra válido': gerado.validacao?.cnab_400_safra_valido,
                    [`${gerado.qtd_titulos} títulos`]: true,
                    [`${gerado.registros} registros`]: true,
                    'todos com 400 posições': gerado.validacao?.todos_400_posicoes,
                    'CPF/CNPJ validados': gerado.validacao?.cpf_cnpj_validados,
                    'CEPs validados': gerado.validacao?.ceps_validados,
                    'multa x instrução coerentes (157-160 / 206-218)': gerado.validacao?.multa_instrucao_coerente,
                    'desconto coerente (180-192 / 388)': gerado.validacao?.desconto_coerente,
                    'trailer conciliado': gerado.validacao?.trailer_conciliado,
                    'ANSI / CRLF': gerado.encoding === 'cp1252' && gerado.line_ending === 'CRLF',
                  }).map(([label, ok]) => (
                    <div key={label} className={ok ? 'text-emerald-800' : 'text-red-700'}>
                      {ok ? '✓' : '✗'} {label}
                    </div>
                  ))}
                  <div className="text-emerald-800">
                    Total do arquivo: <b>{money(gerado.valor_total ?? gerado.total_valor)}</b> ·
                    estado: <b>GERADO</b> (o registro no banco só é confirmado pelo retorno com
                    ocorrência 02)
                  </div>
                  <div className="text-emerald-700">SHA-256: {gerado.hash_sha256}</div>
                </div>
              )}

              {bloqueio && (
                <div className="border border-red-200 bg-red-50 rounded p-3 text-xs space-y-1" data-testid="rem-bloqueio">
                  <div className="text-sm font-semibold text-red-800">{bloqueio.detail}</div>
                  {(bloqueio.erros || []).slice(0, 20).map((e, i) => (
                    <div key={i} className="text-red-700">
                      ERRO CNAB · Registro {e.registro} · Título {e.titulo} · Aluno {e.aluno} ·
                      Matrícula {e.matricula} · Campo {e.campo} · Posições {e.posicoes} · {e.problema}
                    </div>
                  ))}
                  {(bloqueio.detalhe_bloqueados || []).slice(0, 20).map((b, i) => (
                    <div key={i} className="text-red-700">
                      Título {b.titulo} · Aluno {b.aluno} · Matrícula {b.matricula} ·
                      Responsável {b.responsavel || '—'} · Falta: {b.campo_faltante}
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    {bloqueio.pode_gerar_somente_validos && (
                      <Button size="sm" variant="outline" onClick={() => { setBloqueio(null); gerar(true); }}
                        data-testid="rem-gerar-somente-validos">
                        Gerar somente os {bloqueio.validos} válidos
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setBloqueio(null)}>Fechar</Button>
                  </div>
                </div>
              )}

              {validation && (
                <div className="border rounded p-3 bg-muted/30 text-xs space-y-1" data-testid="rem-validation">
                  <div className="font-medium text-sm">
                    Pré-validação · {validation.selecionados} selecionados · {validation.validos} válidos ({money(validation.valor_valido)}) · {validation.bloqueados} bloqueados
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(validation.resumo || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span key={k} className="text-amber-700">{k.replace(/_/g, ' ')}: <b>{v}</b></span>
                    ))}
                  </div>
                  {(validation.config_pendente || []).length > 0 && (
                    <div className="text-red-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Config Safra: {validation.config_pendente.join(', ')}</div>
                  )}
                  {(validation.detalhe_bloqueados || []).slice(0, 12).map((b) => (
                    <div key={b.id} className="text-muted-foreground">• {b.aluno_nome || b.id}: {(b.motivos || []).join(' · ')}</div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1150px] text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className="text-left p-2">Aluno + turma</th>
                      <th className="text-left p-2">Responsável + CPF/CNPJ</th>
                      <th className="text-left p-2">Descrição</th>
                      <th className="text-center p-2">Parc.</th>
                      <th className="text-right p-2">Original</th>
                      <th className="text-right p-2">Desconto</th>
                      <th className="text-right p-2">Cobrança</th>
                      <th className="text-left p-2">Vencimento</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Origem</th>
                      <th className="text-left p-2">Situação Safra</th>
                      <th className="text-left p-2">Pendência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.id} className={`border-t ${i.bucket !== 'elegivel' ? 'bg-amber-50/40' : ''}`} data-testid={`rem-row-${i.id}`}>
                        <td className="p-2">
                          <input type="checkbox" checked={sel.has(i.id)} disabled={i.bucket !== 'elegivel'}
                            onChange={() => toggle(i.id)} data-testid={`rem-check-${i.id}`} />
                        </td>
                        <td className="p-2">{i.aluno_nome || <span className="text-red-600">sem aluno</span>}
                          <div className="text-[10px] text-muted-foreground">{i.turma_nome || '—'}{i.aluno_matricula ? ` · mat. ${i.aluno_matricula}` : ''}</div>
                        </td>
                        <td className="p-2">{i.responsavel_nome || '—'}<div className="text-[10px] text-muted-foreground">{i.responsavel_cpf || 'sem CPF/CNPJ'}</div></td>
                        <td className="p-2">{i.descricao || '—'}</td>
                        <td className="p-2 text-center">{i.parcela || '—'}{i.parcelas_total ? `/${i.parcelas_total}` : ''}</td>
                        <td className="p-2 text-right font-mono">{money(i.valor_original)}</td>
                        <td className="p-2 text-right font-mono text-green-700">{i.desconto > 0 ? `- ${money(i.desconto)}` : '—'}</td>
                        <td className="p-2 text-right font-mono font-semibold">{money(i.valor_final)}</td>
                        <td className="p-2">{i.vencimento ? dt(i.vencimento) : <span className="text-red-600">inválido</span>}</td>
                        <td className="p-2">{i.status || '—'}</td>
                        <td className="p-2">{i.origem}</td>
                        <td className="p-2">{i.situacao_safra}{i.nosso_numero ? <div className="text-[10px]">NN {i.nosso_numero}</div> : null}</td>
                        <td className="p-2 text-amber-700">{(i.motivos || []).join(' · ') || '—'}</td>
                      </tr>
                    ))}
                    {!items.length && !loading && (
                      <tr><td colSpan={13} className="p-6 text-center text-muted-foreground">Nenhum título para os filtros atuais</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span>{data?.total || 0} títulos · página {data?.page || 1} de {data?.total_pages || 1}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={(data?.page || 1) <= 1} onClick={() => setPage(page - 1)} data-testid="rem-prev">Anterior</Button>
                  <Button size="sm" variant="outline" disabled={(data?.page || 1) >= (data?.total_pages || 1)} onClick={() => setPage(page + 1)} data-testid="rem-next">Próxima</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'hist' && (
        <Card>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-xs" data-testid="rem-hist-table">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2">Lote</th>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">Usuário</th>
                    <th className="text-center p-2">Títulos</th>
                    <th className="text-right p-2">Valor</th>
                    <th className="text-left p-2">Arquivo</th>
                    <th className="text-center p-2">Env. / Aceit. / Rej. / Ret.</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {remessas.map((r) => (
                    <tr key={r.id} className="border-t" data-testid={`rem-hist-${r.id}`}>
                      <td className="p-2">{r.remessa_num || '—'}</td>
                      <td className="p-2">{String(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="p-2">{r.created_by || '—'}</td>
                      <td className="p-2 text-center">{r.qtd_titulos ?? '—'}</td>
                      <td className="p-2 text-right font-mono">{money(r.valor_total)}</td>
                      <td className="p-2">{r.filename}</td>
                      <td className="p-2 text-center">{r.enviados ?? 0} / {r.aceitos ?? 0} / {r.rejeitados ?? 0} / {r.retornados ?? 0}</td>
                      <td className="p-2"><Badge variant="outline">{r.status}</Badge></td>
                      <td className="p-2">
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/dnapay/safra/remessa/${r.id}/download`, '_blank')} data-testid={`rem-dl-${r.id}`}>
                            <FileDown className="w-3 h-3 mr-1" /> .REM
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => verTitulos(r)} data-testid={`rem-view-${r.id}`}>
                            <FileText className="w-3 h-3 mr-1" /> Títulos
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => reprocessar(r)} data-testid={`rem-reproc-${r.id}`}>Reprocessar</Button>
                          {!['revertida', 'cancelada', 'cancelled'].includes(String(r.status || '').toLowerCase()) && (
                            <Button size="sm" variant="outline" className="text-amber-700" onClick={() => reverter(r)} data-testid={`rem-revert-${r.id}`}>
                              <Undo2 className="w-3 h-3 mr-1" /> Reverter
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => excluir(r)} data-testid={`rem-delete-${r.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Excluir
                          </Button>
                          {r.status === 'generated' && (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => cancelar(r)} data-testid={`rem-cancel-${r.id}`}>
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!remessas.length && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Nenhuma remessa gerada</td></tr>}
                </tbody>
              </table>
            </div>

            {detalhe && (
              <div className="mt-4 border rounded p-3 text-xs space-y-1" data-testid="rem-detalhe">
                <div className="flex justify-between items-center">
                  <b>Títulos da remessa {detalhe.remessa?.remessa_num} · {detalhe.titulos.length}</b>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-700 font-medium" data-testid="rem-boletos-prontos">
                      {detalhe.boletos_prontos ?? 0} boleto(s) pronto(s)
                    </span>
                    {(detalhe.boletos_pendentes ?? 0) > 0 && (
                      <span className="text-amber-700" data-testid="rem-boletos-pendentes">
                        {detalhe.boletos_pendentes} aguardando
                      </span>
                    )}
                    <Button size="sm" variant="outline" onClick={() => baixarBoletosLote(detalhe)}
                      data-testid="rem-boletos-lote">
                      <FileDown className="w-3.5 h-3.5 mr-1" /> Baixar boletos em lote
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => enviarBoletosRemessa(detalhe)}
                      data-testid="rem-boletos-email">
                      <Mail className="w-3.5 h-3.5 mr-1" /> Enviar por e-mail
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDetalhe(null)}>Fechar</Button>
                  </div>
                </div>
                {detalhe.titulos.slice(0, 60).map((t) => (
                  <div key={t.id} className="flex gap-3">
                    <span className="w-52 truncate">{t.aluno_nome}</span>
                    <span className="w-40 truncate">{t.responsavel_nome || '—'}</span>
                    <span className="w-20 text-right font-mono">{money(t.valor_final)}</span>
                    <span className="w-20">{dt(t.vencimento)}</span>
                    <span className="w-24">NN {t.nosso_numero || '—'}</span>
                    <span className="w-24">{ESTADO_REMESSA[t.remittance_status] || t.remittance_status || '—'}</span>
                    {t.remittance_rejection_reason && (
                      <span className="text-red-700">
                        {t.remittance_rejection_code} · {t.remittance_rejection_reason}
                      </span>
                    )}
                  </div>
                ))}
                {(detalhe.erros || []).length > 0 && (
                  <div className="pt-2 border-t">
                    <b className="text-amber-700">Bloqueados nesta geração:</b>
                    {detalhe.erros.slice(0, 30).map((e, idx) => (
                      <div key={idx} className="text-muted-foreground">• {e.aluno_nome || e.id}: {(e.motivos || []).join(' · ')}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
